import React, { useState } from "react";
import useApi from "@/hooks/useApi";

interface UserResult {
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  displayName: string;
  sites: {
    siteId: number;
    companyName: string;
    companyURL: string;
    isAdministrator: boolean;
    isAgent: boolean;
    isManager: boolean;
    isActive: boolean;
    hasLicense: boolean;
  }[];
}

interface SiteResult {
  siteId: number;
  companyName: string;
  companyURL: string;
  dateCreated: string | null;
  phoneNumber: string | null;
  city: string | null;
  region: string | null;
  isTestSite: boolean | null;
  admins: {
    userId: number;
    userName: string;
    firstName: string;
    lastName: string;
  }[];
}

interface AdminUser {
  userSiteId: number;
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  isAdministrator: boolean;
  isAgent: boolean;
  isManager: boolean;
}

type Tab = "user-lookup" | "site-lookup" | "admin-users" | "integration-logs";

export default function SupportTools() {
  const [tab, setTab] = useState<Tab>("user-lookup");

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Support Tools</h1>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {(
          [
            { id: "user-lookup", label: "User Lookup" },
            { id: "site-lookup", label: "Site Lookup" },
            { id: "admin-users", label: "Admin Users" },
            { id: "integration-logs", label: "Integration Logs" },
          ] as { id: Tab; label: string }[]
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "user-lookup" && <UserLookupTab />}
      {tab === "site-lookup" && <SiteLookupTab />}
      {tab === "admin-users" && <AdminUsersTab />}
      {tab === "integration-logs" && <IntegrationLogsTab />}
    </div>
  );
}

function UserLookupTab() {
  const api = useApi();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<UserResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<UserResult | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  async function search(e: React.FormEvent) {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setError("");
    setSelected(null);
    try {
      const data: UserResult[] = await (
        await api.admin.get(`SupportTools/users/search?q=${encodeURIComponent(q)}`)
      ).json();
      setResults(data);
    } catch (err) {
      setError(`Search failed: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  async function changeUsername() {
    if (!selected || !newEmail.trim()) return;
    setSaving(true);
    setSaveMsg("");
    try {
      await api.admin.post(`SupportTools/users/${selected.userId}/change-username`, {
        newEmail,
      });
      setSaveMsg(`Username updated to ${newEmail}`);
      setSelected({ ...selected, userName: newEmail });
      setResults(results.map((r) => (r.userId === selected.userId ? { ...r, userName: newEmail } : r)));
    } catch (err) {
      const body = err instanceof Response ? await err.json().catch(() => ({})) : {};
      setSaveMsg(`Error: ${body?.error ?? String(err)}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <form onSubmit={search} className="flex gap-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email, first name, or last name"
          className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50"
        >
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {results.length > 0 && (
        <div className="border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Sites</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {results.map((u) => (
                <tr key={u.userId} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-400">{u.userId}</td>
                  <td className="px-4 py-2 font-mono text-xs">{u.userName}</td>
                  <td className="px-4 py-2">
                    {u.firstName} {u.lastName}
                  </td>
                  <td className="px-4 py-2 text-gray-500">{u.sites.length} site{u.sites.length !== 1 ? "s" : ""}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => {
                        setSelected(u);
                        setNewEmail(u.userName);
                        setSaveMsg("");
                      }}
                      className="text-[var(--color-primary)] text-xs hover:underline"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="border border-gray-200 rounded p-4 space-y-4 bg-gray-50">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium text-gray-900">
                {selected.firstName} {selected.lastName}
              </p>
              <p className="text-sm text-gray-500 font-mono">{selected.userName}</p>
              <p className="text-xs text-gray-400">User ID: {selected.userId}</p>
            </div>
            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xs">
              ✕ Close
            </button>
          </div>

          {selected.sites.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Sites</p>
              <div className="space-y-1">
                {selected.sites.map((s) => (
                  <div key={s.siteId} className="flex items-center gap-3 text-sm">
                    <span className="font-mono text-xs text-gray-400 w-14">{s.siteId}</span>
                    <span className="flex-1">{s.companyName}</span>
                    <span className="text-xs text-gray-400">{s.companyURL}</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        s.isAdministrator
                          ? "bg-blue-100 text-blue-700"
                          : s.isManager
                          ? "bg-purple-100 text-purple-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {s.isAdministrator ? "Admin" : s.isManager ? "Manager" : "Agent"}
                    </span>
                    {!s.isActive && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Inactive</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Change Username / Email</p>
            <div className="flex gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
              <button
                onClick={changeUsername}
                disabled={saving || newEmail === selected.userName}
                className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50"
              >
                {saving ? "Saving…" : "Update"}
              </button>
            </div>
            {saveMsg && (
              <p className={`text-sm mt-1 ${saveMsg.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>
                {saveMsg}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SiteLookupTab() {
  const api = useApi();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SiteResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);

  async function search(e: React.FormEvent) {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setError("");
    try {
      const data: SiteResult[] = await (
        await api.admin.get(`SupportTools/sites/search?q=${encodeURIComponent(q)}`)
      ).json();
      setResults(data);
    } catch (err) {
      setError(`Search failed: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      <form onSubmit={search} className="flex gap-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by site ID, company name, or URL"
          className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50"
        >
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {results.length > 0 && (
        <div className="border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-2 text-left">Site ID</th>
                <th className="px-4 py-2 text-left">Company</th>
                <th className="px-4 py-2 text-left">URL</th>
                <th className="px-4 py-2 text-left">Created</th>
                <th className="px-4 py-2 text-left">Admins</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {results.map((s) => (
                <>
                  <tr key={s.siteId} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-gray-400">{s.siteId}</td>
                    <td className="px-4 py-2 font-medium">
                      {s.companyName}
                      {s.isTestSite && (
                        <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full">
                          Test
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{s.companyURL}</td>
                    <td className="px-4 py-2 text-gray-400 text-xs">
                      {s.dateCreated ? new Date(s.dateCreated).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-2 text-gray-500">{s.admins.length}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => setExpanded(expanded === s.siteId ? null : s.siteId)}
                        className="text-[var(--color-primary)] text-xs hover:underline"
                      >
                        {expanded === s.siteId ? "Collapse" : "Details"}
                      </button>
                    </td>
                  </tr>
                  {expanded === s.siteId && (
                    <tr>
                      <td colSpan={6} className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Details</p>
                            {s.phoneNumber && <p className="text-gray-600">📞 {s.phoneNumber}</p>}
                            {s.city && (
                              <p className="text-gray-600">
                                📍 {s.city}
                                {s.region ? `, ${s.region}` : ""}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Admins</p>
                            {s.admins.length === 0 ? (
                              <p className="text-gray-400 text-xs">None</p>
                            ) : (
                              <div className="space-y-0.5">
                                {s.admins.map((a) => (
                                  <p key={a.userId} className="text-gray-600 font-mono text-xs">
                                    {a.userName}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminUsersTab() {
  const api = useApi();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [addMsg, setAddMsg] = useState("");
  const [removing, setRemoving] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data: AdminUser[] = await (await api.admin.get("SupportTools/admin-users")).json();
      setUsers(data);
      setLoaded(true);
    } catch (err) {
      setError(`Failed to load: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  async function addUser(e: React.FormEvent) {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setAdding(true);
    setAddMsg("");
    try {
      await api.admin.post("SupportTools/admin-users", { email: addEmail });
      setAddMsg(`${addEmail} added successfully.`);
      setAddEmail("");
      await load();
    } catch (err) {
      const body = err instanceof Response ? await err.json().catch(() => ({})) : {};
      setAddMsg(`Error: ${body?.error ?? String(err)}`);
    } finally {
      setAdding(false);
    }
  }

  async function removeUser(userId: number, email: string) {
    if (!confirm(`Remove admin access for ${email}?`)) return;
    setRemoving(userId);
    try {
      await api.admin.delete(`SupportTools/admin-users/${userId}`);
      setUsers(users.filter((u) => u.userId !== userId));
    } catch (err) {
      const body = err instanceof Response ? await err.json().catch(() => ({})) : {};
      alert(`Error: ${body?.error ?? String(err)}`);
    } finally {
      setRemoving(null);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Manage who has access to Velaro superadmin tools (site 1032 members).
      </p>

      {!loaded && (
        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50"
        >
          {loading ? "Loading…" : "Load Admin Users"}
        </button>
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {loaded && (
        <>
          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">User ID</th>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => (
                  <tr key={u.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-400 text-xs">{u.userId}</td>
                    <td className="px-4 py-2 font-mono text-xs">{u.userName}</td>
                    <td className="px-4 py-2">
                      {u.firstName} {u.lastName}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          u.isAdministrator ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {u.isAdministrator ? "Admin" : u.isManager ? "Manager" : "Agent"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => removeUser(u.userId, u.userName)}
                        disabled={removing === u.userId}
                        className="text-red-500 text-xs hover:underline disabled:opacity-50"
                      >
                        {removing === u.userId ? "Removing…" : "Remove"}
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-400 text-sm">
                      No admin users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Add Admin User</p>
            <form onSubmit={addUser} className="flex gap-2">
              <input
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="velaro email address"
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
              <button
                type="submit"
                disabled={adding || !addEmail.trim()}
                className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50"
              >
                {adding ? "Adding…" : "Add"}
              </button>
            </form>
            {addMsg && (
              <p className={`text-sm mt-1 ${addMsg.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>
                {addMsg}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface IntegrationLogEntry {
  id: number;
  integration: string | null;
  severity: string | null;
  category: string | null;
  message: string | null;
  additionalInfo: string | null;
  suppressedCount: number;
  createdAt: string;
}

interface IntegrationLogsResponse {
  items: IntegrationLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  integrations: string[];
}

const SEVERITY_COLORS: Record<string, string> = {
  Error:   "bg-red-100 text-red-700",
  Warning: "bg-yellow-100 text-yellow-700",
  Info:    "bg-blue-100 text-blue-700",
  Debug:   "bg-gray-100 text-gray-600",
};

function IntegrationLogsTab() {
  const api = useApi();
  const [siteIdInput, setSiteIdInput] = useState("");
  const [siteId, setSiteId] = useState<number | null>(null);
  const [integration, setIntegration] = useState("");
  const [severity, setSeverity] = useState("All");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<IntegrationLogsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function load(targetSiteId: number, pg = 1) {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: String(pg), pageSize: "50" });
      if (integration) params.set("integration", integration);
      if (severity && severity !== "All") params.set("severity", severity);
      const resp = await api.admin.get(
        `SupportTools/sites/${targetSiteId}/integration-logs?${params}`
      );
      const result: IntegrationLogsResponse = await resp.json();
      setData(result);
      setPage(pg);
    } catch (err) {
      setError(`Failed to load: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    const id = parseInt(siteIdInput.trim(), 10);
    if (!id || isNaN(id)) { setError("Enter a valid numeric site ID."); return; }
    setSiteId(id);
    load(id, 1);
  }

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        View integration event logs for any customer site.
      </p>

      <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Site ID</label>
          <input
            type="number"
            value={siteIdInput}
            onChange={(e) => setSiteIdInput(e.target.value)}
            placeholder="e.g. 1234"
            className="border border-gray-300 rounded px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          />
        </div>

        {data?.integrations && data.integrations.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Integration</label>
            <select
              value={integration}
              onChange={(e) => setIntegration(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              <option value="">All integrations</option>
              {data.integrations.map((i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Severity</label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          >
            <option value="All">All</option>
            <option value="Error">Error</option>
            <option value="Warning">Warning</option>
            <option value="Info">Info</option>
            <option value="Debug">Debug</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50 self-end"
        >
          {loading ? "Loading…" : "Load Logs"}
        </button>

        {siteId && data && (
          <button
            type="button"
            onClick={() => load(siteId, page)}
            disabled={loading}
            className="px-3 py-2 border border-gray-300 rounded text-sm text-gray-600 disabled:opacity-50 self-end hover:bg-gray-50"
          >
            Refresh
          </button>
        )}
      </form>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {data && (
        <>
          <div className="text-xs text-gray-400">
            {data.total.toLocaleString()} total entries for site {siteId}
          </div>

          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">When</th>
                  <th className="px-4 py-2 text-left">Severity</th>
                  <th className="px-4 py-2 text-left">Integration</th>
                  <th className="px-4 py-2 text-left">Category</th>
                  <th className="px-4 py-2 text-left">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                      No log entries found.
                    </td>
                  </tr>
                )}
                {data.items.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-2 text-gray-400 text-xs whitespace-nowrap">
                      {new Date(entry.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2">
                      {entry.severity && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEVERITY_COLORS[entry.severity] ?? "bg-gray-100 text-gray-600"}`}>
                          {entry.severity}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">{entry.integration ?? "—"}</td>
                    <td className="px-4 py-2 text-xs text-gray-400">{entry.category ?? "—"}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">
                      <div>{entry.message}</div>
                      {entry.additionalInfo && (
                        <div className="text-gray-400 mt-0.5">{entry.additionalInfo}</div>
                      )}
                      {entry.suppressedCount > 0 && (
                        <div className="text-gray-400 mt-0.5">+{entry.suppressedCount} suppressed</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => siteId && load(siteId, page - 1)}
                  disabled={page === 1 || loading}
                  className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50"
                >
                  ← Prev
                </button>
                <button
                  onClick={() => siteId && load(siteId, page + 1)}
                  disabled={page === totalPages || loading}
                  className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
